import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { ClinicWorkspaceShell } from '../components/clinic/ClinicWorkspaceShell';
import { ClinicOverviewPanel } from '../components/clinic/ClinicOverviewPanel';
import { ClinicAnalyticsPanel } from '../components/clinic/ClinicAnalyticsPanel';
import { ClinicInventoryPanel } from '../components/clinic/ClinicInventoryPanel';
import { ClinicLabsPanel } from '../components/clinic/ClinicLabsPanel';
import { ClinicAppointmentsPanel } from '../components/clinic/ClinicAppointmentsPanel';
import InteractiveCalendar from '../components/clinic/InteractiveCalendar';
import ClinicSocialPanel from '../components/clinic/ClinicSocialPanel';
import { ClinicHistoriesPanel } from '../components/clinic/ClinicHistoriesPanel';
import { ClinicContentPanel } from '../components/clinic/ClinicContentPanel';
import { ClinicPatientsPanel } from '../components/clinic/ClinicPatientsPanel';
import { ClinicQuickConsultPanel } from '../components/clinic/ClinicQuickConsultPanel';
import { ClinicOperationsPanel } from '../components/clinic/ClinicOperationsPanel';
import { MlmConnections } from '../components/mlm/MlmConnections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLINIC_ALLOWED_ROLES = ['admin', 'doctora', 'enfermera', 'administradora', 'community_manager'];

const buildFormData = (payload) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    formData.append(key, value);
  });
  return formData;
};

export default function ClinicWorkspacePage() {
  const navigate = useNavigate();
  const { user, loading, logout, authFetch } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState('');
  const [consultMode, setConsultMode] = useState(false);
  const [appAccess, setAppAccess] = useState(null);
  const [appRequests, setAppRequests] = useState([]);
  const [socialData, setSocialData] = useState({ platforms: [], connections: {} });

  const loadSocialConnections = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/clinic/social-connections`);
      const data = await response.json();
      if (response.ok) setSocialData({ platforms: data.platforms || [], connections: data.connections || {} });
    } catch (error) {
      // silencioso
    }
  }, [authFetch]);

  const saveSocialConnection = useCallback(async (platform, payload) => {
    setSaving(`social-${platform}`);
    try {
      const response = await authFetch(`${API}/clinic/social-connections/${platform}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo guardar la conexión');
      toast.success(`Conexión de ${platform} guardada`);
      await loadSocialConnections();
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar la conexión');
    } finally {
      setSaving('');
    }
  }, [authFetch, loadSocialConnections]);

  // API wrapper para conexiones Nango de la clínica
  const clinicApi = useCallback(async (path, options = {}, opts = {}) => {
    try {
      const res = await authFetch(`${API}/clinic${path}`, { ...options, headers: { 'Content-Type': 'application/json', ...(options.headers || {}) } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error en la operación');
      return data;
    } catch (err) {
      toast.error(err.message);
      throw err;
    }
  }, [authFetch]);

  const loadAppRequests = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/clinic-app/appointment-requests`);
      const data = await response.json();
      if (response.ok) setAppRequests(data.requests || []);
    } catch (error) {
      // silencioso: solicitudes de la app no bloquean el panel
    }
  }, [authFetch]);

  const loadWorkspace = useCallback(async () => {
    setFetching(true);
    try {
      const response = await authFetch(`${API}/clinic/workspace`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo cargar la clínica');
      setWorkspace(data);
      setActiveSection((current) => data.sections.some((section) => section.id === current) ? current : data.sections[0]?.id || 'overview');
    } catch (error) {
      toast.error(error.message || 'Error cargando la clínica');
    } finally {
      setFetching(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!CLINIC_ALLOWED_ROLES.includes(user.role)) {
      navigate(user.role === 'admin' ? '/admin' : '/panel');
      return;
    }
    loadWorkspace();
    loadAppRequests();
    loadSocialConnections();
  }, [loading, user, navigate, loadWorkspace, loadAppRequests, loadSocialConnections]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1180) {
      setConsultMode(true);
    }
  }, []);

  const runAction = useCallback(async ({ key, request, successMessage }) => {
    setSaving(key);
    try {
      const response = await request();
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Operación no completada');
      toast.success(successMessage);
      await loadWorkspace();
      return true;
    } catch (error) {
      toast.error(error.message || 'No se pudo completar la operación');
      return false;
    } finally {
      setSaving('');
    }
  }, [loadWorkspace]);

  const openReport = useCallback((url) => {
    if (!url) {
      toast.error('Reporte no disponible todavía');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const toggleConsultMode = useCallback(() => {
    setConsultMode((current) => {
      const next = !current;
      setActiveSection((section) => {
        if (next) return 'consult';
        if (section === 'consult') return 'overview';
        return section;
      });
      return next;
    });
  }, []);

  const quickCloseConsultation = useCallback(async (payload) => {
    setSaving('quick-consult-create');
    try {
      const historyPayload = {
        ...payload,
        status: payload.status || 'cerrado',
      };
      const historyResponse = await authFetch(`${API}/clinic/clinical-histories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(historyPayload),
      });
      const historyData = await historyResponse.json();
      if (!historyResponse.ok) throw new Error(historyData.detail || 'No se pudo guardar la consulta');

      if (payload.appointment_id) {
        const appointmentResponse = await authFetch(`${API}/clinic/appointments/${payload.appointment_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'completada' }),
        });
        const appointmentData = await appointmentResponse.json();
        if (!appointmentResponse.ok) throw new Error(appointmentData.detail || 'No se pudo cerrar la cita asociada');
      }

      toast.success('Consulta cerrada y guardada');
      await loadWorkspace();
      setActiveSection('patients');
      return true;
    } catch (error) {
      toast.error(error.message || 'No se pudo cerrar la consulta');
      return false;
    } finally {
      setSaving('');
    }
  }, [authFetch, loadWorkspace]);

  const runReminders = useCallback(async () => {
    setSaving('run-reminders');
    try {
      const response = await authFetch(`${API}/clinic-app/reminders/run`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudieron enviar los recordatorios');
      toast.success(data.reminders_sent > 0 ? `${data.reminders_sent} recordatorio(s) enviados a pacientes con cita en las próximas 24h` : 'No hay citas en las próximas 24h pendientes de recordatorio');
      await loadWorkspace();
    } catch (error) {
      toast.error(error.message || 'No se pudieron enviar los recordatorios');
    } finally {
      setSaving('');
    }
  }, [authFetch, loadWorkspace]);

  const generateAppAccess = useCallback(async (patient) => {
    setSaving(`app-access-${patient.patient_key}`);
    try {
      const response = await authFetch(`${API}/clinic-app/access-codes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ patient_key: patient.patient_key, patient_name: patient.patient_name, patient_phone: patient.patient_phone, patient_dni: patient.patient_dni }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo generar el acceso');
      setAppAccess(data);
      toast.success('Acceso a la app del paciente generado');
    } catch (error) {
      toast.error(error.message || 'No se pudo generar el acceso');
    } finally {
      setSaving('');
    }
  }, [authFetch]);

  const downloadHistoryPdf = useCallback(async (history) => {
    setSaving(`pdf-${history.id}`);
    try {
      const response = await authFetch(`${API}/clinic/clinical-histories/${history.id}/pdf`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'No se pudo generar el PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evolucion-${(history.patient_name || 'paciente').replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error(error.message || 'No se pudo generar el PDF');
    } finally {
      setSaving('');
    }
  }, [authFetch]);

  const sendWelcomeEmail = useCallback(async (patientKey, email) => {
    setSaving(`welcome-${patientKey}`);
    try {
      const response = await authFetch(`${API}/clinic-app/send-welcome-email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ patient_key: patientKey, email }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo enviar el email');
      toast.success(`Email de bienvenida enviado a ${data.email}`);
    } catch (error) {
      toast.error(error.message || 'No se pudo enviar el email');
    } finally {
      setSaving('');
    }
  }, [authFetch]);

  const resolveAppRequest = useCallback(async (requestId, payload) => {
    setSaving(`app-request-${requestId}`);
    try {
      const response = await authFetch(`${API}/clinic-app/appointment-requests/${requestId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo actualizar la solicitud');
      toast.success(payload.action === 'confirm' ? 'Cita confirmada y añadida a la agenda' : 'Solicitud rechazada');
      await Promise.all([loadWorkspace(), loadAppRequests()]);
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar la solicitud');
    } finally {
      setSaving('');
    }
  }, [authFetch, loadWorkspace, loadAppRequests]);

  const actions = useMemo(() => ({
    createInventory: (payload) => runAction({
      key: 'inventory-create',
      successMessage: 'Producto guardado',
      request: () => authFetch(`${API}/clinic/inventory`, { method: 'POST', body: buildFormData(payload) }),
    }),
    quickAdjustInventory: (itemId, stockCount) => runAction({
      key: `inventory-adjust-${itemId}`,
      successMessage: 'Stock actualizado',
      request: () => authFetch(`${API}/clinic/inventory/${itemId}`, { method: 'PATCH', body: buildFormData({ stock_count: stockCount }) }),
    }),
    createAppointment: (payload) => runAction({
      key: 'appointment-create',
      successMessage: 'Cita guardada',
      request: () => authFetch(`${API}/clinic/appointments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    updateAppointment: (appointmentId, payload) => runAction({
      key: `appointment-update-${appointmentId}`,
      successMessage: 'Cita actualizada',
      request: () => authFetch(`${API}/clinic/appointments/${appointmentId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    deleteAppointment: (appointmentId) => runAction({
      key: `appointment-delete-${appointmentId}`,
      successMessage: 'Cita eliminada',
      request: () => authFetch(`${API}/clinic/appointments/${appointmentId}`, { method: 'DELETE' }),
    }),
    createHistory: (payload) => runAction({
      key: 'history-create',
      successMessage: 'Historial guardado',
      request: () => authFetch(`${API}/clinic/clinical-histories`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    updateHistory: (historyId, payload) => runAction({
      key: `history-update-${historyId}`,
      successMessage: 'Historial actualizado',
      request: () => authFetch(`${API}/clinic/clinical-histories/${historyId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    createTask: (payload) => runAction({
      key: 'content-create',
      successMessage: 'Pieza guardada',
      request: () => authFetch(`${API}/clinic/content-tasks`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    updateTask: (taskId, payload) => runAction({
      key: `content-update-${taskId}`,
      successMessage: 'Hub de contenido actualizado',
      request: () => authFetch(`${API}/clinic/content-tasks/${taskId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    createOperationTask: (payload) => runAction({
      key: 'operation-create',
      successMessage: 'Tarea diaria creada',
      request: () => authFetch(`${API}/clinic/operation-tasks`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    updateOperationTask: (taskId, payload) => runAction({
      key: `operation-update-${taskId}`,
      successMessage: 'Tarea diaria actualizada',
      request: () => authFetch(`${API}/clinic/operation-tasks/${taskId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
    }),
    uploadTemplate: (file) => runAction({
      key: 'template-upload',
      successMessage: 'Excel clínico subido',
      request: () => {
        const formData = new FormData();
        formData.append('file', file);
        return authFetch(`${API}/clinic/template-upload`, { method: 'POST', body: formData });
      },
    }),
    sendFollowUpEmail: (patientKey) => runAction({
      key: `followup-${patientKey}`,
      successMessage: 'Seguimiento enviado por email',
      request: () => authFetch(`${API}/clinic/patients/${patientKey}/follow-up-email`, { method: 'POST' }),
    }),
  }), [authFetch, runAction]);

  if (loading || (fetching && !workspace)) {
    return (
      <main className="min-h-screen bg-[radial-gradient(circle_at_top,_#d8fff8_0%,_#f5f0e7_50%,_#eef2ff_100%)] px-4 py-10" data-testid="clinic-workspace-loading">
        <div className="mx-auto max-w-7xl rounded-[32px] border border-white/60 bg-white/70 p-10 text-center text-slate-600">Cargando espacio clínico…</div>
      </main>
    );
  }

  if (!workspace) {
    return (
      <main className="min-h-screen bg-[radial-gradient(circle_at_top,_#d8fff8_0%,_#f5f0e7_50%,_#eef2ff_100%)] px-4 py-10" data-testid="clinic-workspace-error">
        <div className="mx-auto max-w-4xl rounded-[32px] border border-white/60 bg-white/80 p-10 text-center">
          <p className="text-sm uppercase tracking-[0.24em] text-[#0f766e]">Clínica</p>
          <h1 className="mt-3 text-3xl font-black text-slate-900">No pudimos abrir el panel ahora mismo</h1>
          <button type="button" onClick={loadWorkspace} className="mt-6 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white" data-testid="clinic-workspace-retry-button">Reintentar</button>
        </div>
      </main>
    );
  }

  const canEditAppointments = ['admin', 'doctora', 'enfermera', 'administradora'].includes(workspace.user.role);
  const canEditHistories = ['admin', 'doctora', 'enfermera'].includes(workspace.user.role);
  const canEditContent = ['admin', 'community_manager'].includes(workspace.user.role);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,_#d8fff8_0%,_#f5f0e7_50%,_#eef2ff_100%)] px-4 py-8 sm:px-6 lg:px-8" data-testid="clinic-workspace-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex justify-end gap-3">
          {user?.role === 'admin' && <button type="button" onClick={() => navigate('/admin')} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700" data-testid="clinic-workspace-back-admin-button">Volver a admin</button>}
          <button type="button" onClick={logout} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700" data-testid="clinic-workspace-logout-button">Salir</button>
        </div>

        <ClinicWorkspaceShell workspace={workspace} activeSection={activeSection} onSectionChange={setActiveSection} consultMode={consultMode} onToggleConsultMode={toggleConsultMode} />

        {activeSection === 'overview' && <ClinicOverviewPanel workspace={workspace} appointments={workspace?.appointments || []} consultMode={consultMode} onToggleConsultMode={toggleConsultMode} onOpenReport={openReport} onOpenQuickConsult={() => { setConsultMode(true); setActiveSection('consult'); }} onUploadTemplate={actions.uploadTemplate} saving={saving} />}
        {activeSection === 'analytics' && <ClinicAnalyticsPanel workspace={workspace} />}
        {activeSection === 'consult' && <ClinicQuickConsultPanel workspace={workspace} onSubmitConsultation={quickCloseConsultation} saving={saving} />}
        {activeSection === 'operations' && <ClinicOperationsPanel workspace={workspace} onCreateTask={actions.createOperationTask} onUpdateTask={actions.updateOperationTask} onOpenReport={openReport} saving={saving} />}
        {activeSection === 'patients' && <ClinicPatientsPanel workspace={workspace} onOpenReport={openReport} onSendFollowUpEmail={actions.sendFollowUpEmail} consultMode={consultMode} saving={saving} onGenerateAppAccess={generateAppAccess} appAccess={appAccess} onCloseAppAccess={() => setAppAccess(null)} onSendWelcomeEmail={sendWelcomeEmail} />}
        {activeSection === 'inventory' && (
          <div className="space-y-5">
            <ClinicLabsPanel canManage={['admin', 'doctora', 'administradora', 'enfermera'].includes(workspace?.user?.role)} />
            <ClinicInventoryPanel workspace={workspace} onCreateInventory={actions.createInventory} onQuickAdjust={actions.quickAdjustInventory} saving={saving} />
          </div>
        )}
        {activeSection === 'appointments' && (
          <div className="space-y-5">
            <InteractiveCalendar appointments={workspace?.appointments || []} onCreate={actions.createAppointment} onUpdate={actions.updateAppointment} onDelete={actions.deleteAppointment} canEdit={canEditAppointments} saving={saving} />
            <ClinicAppointmentsPanel workspace={workspace} onCreateAppointment={actions.createAppointment} onUpdateAppointment={actions.updateAppointment} saving={saving} canEdit={canEditAppointments} appRequests={appRequests} onResolveAppRequest={resolveAppRequest} onRunReminders={runReminders} />
          </div>
        )}
        {activeSection === 'histories' && <ClinicHistoriesPanel workspace={workspace} onCreateHistory={actions.createHistory} onUpdateHistory={actions.updateHistory} saving={saving} canEdit={canEditHistories} consultMode={consultMode} onDownloadPdf={downloadHistoryPdf} />}
        {activeSection === 'content' && <ClinicContentPanel workspace={workspace} onCreateTask={actions.createTask} onUpdateTask={actions.updateTask} saving={saving} canEdit={canEditContent} />}
        {activeSection === 'social' && <ClinicSocialPanel connections={socialData.connections} platforms={socialData.platforms} onSave={saveSocialConnection} saving={saving} canManage={['community_manager', 'doctora', 'administradora', 'admin'].includes(workspace?.user?.role)} />}
        {activeSection === 'connections' && <MlmConnections api={clinicApi} />}
      </div>
    </main>
  );
}